/**
 * The WhatsApp deep link to the hosts, with the message already typed.
 *
 * Requests are never a chat (Section 6). Anything that needs a conversation
 * goes to WhatsApp with the words pre-filled, and the guest taps send there.
 *
 * The number comes from VITE_HOSTS_WHATSAPP. It is not a secret, and it is not
 * known yet, so with no number set the link is null and the screen leaves the
 * button out instead of sending a guest to a dead link. The same goes for the
 * stand in 507000000 that .env.example carries until Tim has the real number:
 * a placeholder is treated exactly like no number at all.
 */

/** Digits only, no plus, no spaces, as wa.me wants them. Null when it cannot be a number. */
export function normalizeWhatsAppNumber(raw: string | undefined | null): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  // Shortest real numbers are about eight digits with the country code.
  return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

/**
 * A stand in rather than a phone: six or more zeros at the end, which no real
 * subscriber number has. Catches 507000000 and any other zeroed out example.
 */
export function isPlaceholderNumber(digits: string): boolean {
  return /0{6,}$/.test(digits);
}

export function buildWhatsAppLink(number: string | undefined | null, message: string): string | null {
  const digits = normalizeWhatsAppNumber(number);
  if (!digits || isPlaceholderNumber(digits)) return null;
  const text = message.trim();
  return text
    ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
    : `https://wa.me/${digits}`;
}

/**
 * A link with no number, which opens WhatsApp's own contact picker with the
 * message ready. For text the guest wants to send to someone who is not the
 * hosts, such as a captain or a shop.
 */
export function buildWhatsAppShareLink(message: string): string | null {
  const text = message.trim();
  return text ? `https://wa.me/?text=${encodeURIComponent(text)}` : null;
}

export type RequestMessageInput = {
  /** Already in the guest's language, e.g. "A boat run". */
  typeLabel: string;
  details: string;
  firstName?: string;
  /** Free text such as "Thursday 4 PM". Optional. */
  when?: string;
  /** A short greeting line in the guest's language. */
  greeting: string;
};

/** The pre-filled text. Short, plain, and it says who is writing without a stay code. */
export function buildRequestMessage(input: RequestMessageInput): string {
  const lines: string[] = [];
  const who = input.firstName?.trim();
  lines.push(who ? `${input.greeting} (${who})` : input.greeting);
  lines.push(input.typeLabel);
  if (input.when?.trim()) lines.push(input.when.trim());
  if (input.details.trim()) lines.push(input.details.trim());
  return lines.join('\n');
}
